import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../../lib/api-client';
import { cacheUtils, queryKeys } from '../../lib/query-client';
import { firmApi } from '../../services/api/firm-api';
import {
  ListFirmsQueryParams,
  UpdateBalanceUsageRequest,
  UpdatePlanConfigRequest,
  UpgradePlanRequest,
} from '../../types/firm';

// Hook for listing firms with optional parameters
export function useFirms(params?: ListFirmsQueryParams) {
  return useQuery({
    queryKey: queryKeys.firms.list(params),
    queryFn: () => firmApi.listFirms(params),
    // Enable the query only if we have authentication
    enabled: !!apiClient.hasAuthToken(),
  });
}

// Hook for getting firm pricing details (full details)
export function useFirmPricingDetail(firmId: number | null) {
  return useQuery({
    queryKey: queryKeys.firms.detail(firmId || 0),
    queryFn: () => {
      if (!firmId) throw new Error('Firm ID is required');
      return firmApi.getFirmPricingDetail(firmId);
    },
    enabled: !!firmId && !!apiClient.hasAuthToken(),
  });
}

// Hook for getting plan configuration only
export function usePlanConfig(firmId: number | null) {
  return useQuery({
    queryKey: ['firms', firmId || 0, 'plan-config'],
    queryFn: () => {
      if (!firmId) throw new Error('Firm ID is required');
      return firmApi.getPlanConfig(firmId);
    },
    enabled: !!firmId && !!apiClient.hasAuthToken(),
  });
}

// Hook for getting balance and usage information
export function useBalanceUsage(firmId: number | null) {
  return useQuery({
    queryKey: ['firms', firmId || 0, 'balance-usage'],
    queryFn: async () => {
      if (!firmId) throw new Error('Firm ID is required');
      const result = await firmApi.getBalanceUsage(firmId);
      console.log('balance usage', result);
      return result;
    },
    enabled: !!firmId && !!apiClient.hasAuthToken(),
  });
}

// Hook for creating a new firm
// export function useCreateFirm() {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: firmsApi.createFirm,
//     onSuccess: (newFirm) => {
//       // Invalidate firms list to refetch with new data
//       cacheUtils.invalidateFeature('firms');

//       // Optionally set the new firm in cache
//       queryClient.setQueryData(
//         queryKeys.firms.detail(newFirm.id),
//         newFirm
//       );

//       console.log('Firm created successfully');
//     },
//     onError: (error) => {
//       console.error('Failed to create firm:', error);
//     },
//   });
// }

// Hook for updating a firm
// export function useUpdateFirm() {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: ({ id, data }: { id: string; data: UpdateFirmRequest }) =>
//       firmsApi.updateFirm(id, data),
//     onSuccess: (updatedFirm, variables) => {
//       // Update the specific firm in cache
//       queryClient.setQueryData(
//         queryKeys.firms.detail(variables.id),
//         updatedFirm
//       );

//       // Invalidate firms list to refetch with updated data
//       cacheUtils.invalidateFeature('firms');

//       console.log('Firm updated successfully');
//     },
//     onError: (error) => {
//       console.error('Failed to update firm:', error);
//     },
//   });
// }

// Hook for deleting a firm
// export function useDeleteFirm() {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: firmsApi.deleteFirm,
//     onSuccess: (_, firmId) => {
//       // Remove the firm from cache
//       queryClient.removeQueries({
//         queryKey: queryKeys.firms.detail(firmId),
//       });

//       // Invalidate firms list to refetch without deleted firm
//       cacheUtils.invalidateFeature('firms');

//       console.log('Firm deleted successfully');
//     },
//     onError: (error) => {
//       console.error('Failed to delete firm:', error);
//     },
//   });
// }

// Hook for updating plan configuration
export function useUpdatePlanConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: UpdatePlanConfigRequest }) =>
      firmApi.updatePlanConfig(firmId, data),
    onSuccess: (updatedPlanConfig, variables) => {
      // Invalidate all related queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: queryKeys.firms.detail(variables.firmId) });
      queryClient.invalidateQueries({ queryKey: ['firms', variables.firmId, 'plan-config'] });
      queryClient.invalidateQueries({ queryKey: ['firms', variables.firmId, 'balance-usage'] });

      // Invalidate firms list to refetch with updated data
      cacheUtils.invalidateFeature('firms');

      console.log('Plan configuration updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update plan configuration:', error);
    },
  });
}

// Hook for upgrading firm plan
export function useUpgradePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: UpgradePlanRequest }) => firmApi.upgradePlan(firmId, data),
    onSuccess: (response, variables) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.firms.detail(variables.firmId) });
      queryClient.invalidateQueries({ queryKey: ['firms', variables.firmId, 'plan-config'] });
      queryClient.invalidateQueries({ queryKey: ['firms', variables.firmId, 'balance-usage'] });

      // Invalidate firms list to refetch with updated data
      cacheUtils.invalidateFeature('firms');

      console.log('Plan upgraded successfully:', response.message);
    },
    onError: (error) => {
      console.error('Failed to upgrade plan:', error);
    },
  });
}

// Hook for updating balance usage
export function useUpdateBalanceUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ firmId, data }: { firmId: number; data: UpdateBalanceUsageRequest }) =>
      firmApi.updateBalanceUsage(firmId, data),
    onSuccess: (updatedData, variables) => {
      // Update balance usage cache
      queryClient.setQueryData(['firms', variables.firmId, 'balance-usage'], updatedData);

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.firms.detail(variables.firmId) });

      // Invalidate firms list to refetch with updated data
      cacheUtils.invalidateFeature('firms');

      console.log('Balance usage updated successfully');
    },
    onError: (error) => {
      console.error('Failed to update balance usage:', error);
    },
  });
}

// Hook for refreshing firms data
export function useRefreshFirms() {
  return () => {
    cacheUtils.invalidateFeature('firms');
  };
}
